import { Link } from "@tanstack/react-router";
import {
  ArchiveIcon,
  CloudIcon,
  LaptopIcon,
  PauseIcon,
  PencilIcon,
  PlayIcon,
  ZapIcon,
} from "lucide-react";

import { Button } from "~/components/ui/button";
import { RESPONSIVE_ICON_BUTTON_CLASS, RESPONSIVE_TEXT_BUTTON_CLASS } from "~/lib/touchTarget";
import { formatRelativeTimeLabel } from "~/timestampFormat";
import {
  automationRouteId,
  itemLatestRunLabel,
  itemLocationLabel,
  itemNextRunAt,
  itemProjectName,
  itemPrompt,
  itemStatus,
  itemTriggerLabel,
  type AutomationListItem,
} from "./ForgeAutomationModel";
import type { AutomationDisplayRun } from "./automationRunModel";

interface ForgeAutomationDetailProps {
  readonly item: AutomationListItem;
  readonly projectNames: ReadonlyMap<string, string>;
  readonly runs: readonly AutomationDisplayRun[];
  readonly busy: boolean;
  readonly error: string | null;
  readonly runsError: string | null;
  readonly runsLoading: boolean;
  readonly onArchive: () => void;
  readonly onRetryRuns: () => void;
  readonly onRunNow: () => void;
  readonly onToggle: () => void;
}

function RunRow({ run }: { readonly run: AutomationDisplayRun }) {
  const canOpen = run.environmentId !== null && run.threadId !== null;
  const destinationLabel = canOpen
    ? "Open task"
    : run.status === "failed" || run.status === "cancelled"
      ? "No task created"
      : "Preparing…";
  const content = (
    <span className="grid min-h-touch grid-cols-[minmax(0,1fr)_auto] items-center gap-3 border-b border-border/60 py-2 md:min-h-0 md:py-2">
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium capitalize">
          {run.status.replaceAll("_", " ")}
        </span>
        <span className="mt-0.5 block text-sm text-muted-foreground md:text-xs">
          {run.trigger} · {formatRelativeTimeLabel(run.updatedAt)}
        </span>
        {run.error ? (
          <span className="mt-0.5 block text-sm text-muted-foreground md:text-xs">{run.error}</span>
        ) : null}
      </span>
      <span className="text-xs text-muted-foreground">{destinationLabel}</span>
    </span>
  );
  return canOpen ? (
    <Link
      className="block rounded-md outline-none hover:bg-accent/45 focus-visible:ring-2 focus-visible:ring-ring"
      to="/$environmentId/$threadId"
      params={{ environmentId: run.environmentId, threadId: run.threadId }}
    >
      {content}
    </Link>
  ) : (
    content
  );
}

export function ForgeAutomationDetail(props: ForgeAutomationDetailProps) {
  const active = itemStatus(props.item) === "active";
  const nextRunAt = itemNextRunAt(props.item);
  const LocationIcon = props.item.kind === "cloud" ? CloudIcon : LaptopIcon;
  return (
    <main className="min-h-0 flex-1 overflow-y-auto bg-background text-foreground">
      <div className="mx-auto w-full max-w-5xl px-4 py-7 sm:px-6 md:py-10">
        {/* No breadcrumb here: the bar shows `Automations / <name>`, with the
            name published by ForgeAutomationDetailPage. */}
        <div className="flex flex-col gap-5 border-b border-border/60 pb-7 sm:flex-row sm:items-start sm:justify-between">
          {/* No title: the page publishes this exact name to the bar. */}
          <div className="min-w-0">
            <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <LocationIcon className="size-4" />
              {itemLocationLabel(props.item)} · {itemProjectName(props.item, props.projectNames)}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              className={RESPONSIVE_TEXT_BUTTON_CLASS}
              render={
                <Link
                  to="/automations/$automationId/edit"
                  params={{ automationId: automationRouteId(props.item) }}
                />
              }
              size="sm"
              variant="outline"
            >
              <PencilIcon />
              Edit
            </Button>
            <Button
              className={RESPONSIVE_TEXT_BUTTON_CLASS}
              disabled={props.busy || !active}
              size="sm"
              onClick={props.onRunNow}
            >
              <ZapIcon />
              Run now
            </Button>
            <Button
              aria-label={active ? "Pause automation" : "Resume automation"}
              className={RESPONSIVE_ICON_BUTTON_CLASS}
              disabled={props.busy}
              size="icon"
              variant="ghost"
              onClick={props.onToggle}
            >
              {active ? <PauseIcon /> : <PlayIcon />}
            </Button>
            <Button
              aria-label="Archive automation"
              className={RESPONSIVE_ICON_BUTTON_CLASS}
              disabled={props.busy}
              size="icon"
              variant="ghost"
              onClick={props.onArchive}
            >
              <ArchiveIcon />
            </Button>
          </div>
        </div>

        {props.error ? (
          <p className="mt-5 text-sm text-destructive" role="alert">
            {props.error}
          </p>
        ) : null}

        <div className="grid gap-10 py-8 lg:grid-cols-[minmax(0,1fr)_280px]">
          <section aria-labelledby="automation-instructions-detail-heading" className="min-w-0">
            <h2 id="automation-instructions-detail-heading" className="text-sm font-semibold">
              Instructions
            </h2>
            <p className="mt-3 whitespace-pre-wrap text-sm leading-7 text-foreground/90">
              {itemPrompt(props.item)}
            </p>
          </section>
          <aside className="space-y-6">
            <section aria-labelledby="automation-details-heading">
              <h2 id="automation-details-heading" className="text-sm font-semibold">
                Details
              </h2>
              <dl className="mt-3 space-y-2.5 text-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">Status</dt>
                  <dd className="capitalize">{itemStatus(props.item)}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">Repeats</dt>
                  <dd className="text-right">{itemTriggerLabel(props.item)}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">Next run</dt>
                  <dd>{active && nextRunAt ? formatRelativeTimeLabel(nextRunAt) : "—"}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">Last ran</dt>
                  <dd>{itemLatestRunLabel(props.item)}</dd>
                </div>
              </dl>
            </section>
          </aside>
        </div>

        <section
          aria-labelledby="automation-runs-heading"
          className="border-t border-border/60 pt-7"
        >
          <h2 id="automation-runs-heading" className="text-sm font-semibold">
            Runs
          </h2>
          <div className="mt-2 border-t border-border/60">
            {props.runsError ? (
              <div className="flex flex-wrap items-center justify-between gap-3 py-5">
                <p className="text-sm text-destructive" role="alert">
                  {props.runsError}
                </p>
                <Button size="sm" variant="outline" onClick={props.onRetryRuns}>
                  Retry
                </Button>
              </div>
            ) : props.runsLoading ? (
              <p className="py-6 text-sm text-muted-foreground" role="status">
                Loading runs…
              </p>
            ) : props.runs.length > 0 ? (
              props.runs.map((run) => <RunRow key={run.id} run={run} />)
            ) : (
              <p className="py-6 text-sm text-muted-foreground">No runs yet.</p>
            )}
          </div>
        </section>
      </div>
    </main>
  );
}
